import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import {
  supplierWeddingTypes,
  supplierVibeTags,
  supplierFormalityOptions,
  supplierStyleOptions
} from '../../../config/supplierTaxonomy';

const STYLE_PREFERENCES_MIN = 1;
const STYLE_PREFERENCES_MAX = 5;
const STYLE_PREFERENCE_LABELS = {
  weddingType: 'wedding types',
  vibeTags: 'style vibe tags'
};

const formatOptionLabel = (value) =>
  String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

function Step5Style({ formData, businessId, onComplete, onError }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [styleData, setStyleData] = useState({
    weddingType: [],
    vibeTags: [],
    formality: ''
  });
  const [genreInput, setGenreInput] = useState('');

  const category = formData.category;

  useEffect(() => {
    // Load existing style data if available
    if (formData.styleData) {
      setStyleData(formData.styleData);
    } else if (formData.styleVibe && formData.styleVibe.length > 0) {
      // Pre-populate with scraped style/vibe data
      const category = formData.category;
      const options = supplierStyleOptions[category] || {};
      
      // Map styleVibe to appropriate fields based on category
      const prePopulatedData = {};
      
      if (options.styleSpecialization) {
        prePopulatedData.styleSpecialization = formData.styleVibe.filter(vibe => 
          options.styleSpecialization.includes(vibe)
        );
      }
      if (options.capacitySpecialization) {
        prePopulatedData.capacitySpecialization = formData.styleVibe.filter(vibe =>
          options.capacitySpecialization.includes(vibe)
        );
      }
      if (options.style) {
        prePopulatedData.style = formData.styleVibe.filter(vibe => 
          options.style.includes(vibe)
        );
      }
      if (options.services) {
        prePopulatedData.services = formData.styleVibe.filter(vibe => 
          options.services.includes(vibe)
        );
      }
      
      if (Object.keys(prePopulatedData).length > 0) {
        setStyleData(prePopulatedData);
      }
    }
  }, [formData]);

  const getOptionsForCategory = () => {
    return supplierStyleOptions[category] || {};
  };

  const handleArrayToggle = (field, value) => {
    setStyleData(prev => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleStylePreferenceToggle = (field, value) => {
    setError('');
    setStyleData(prev => {
      const currentArray = prev[field] || [];
      const isRemoving = currentArray.includes(value);

      if (!isRemoving && currentArray.length >= STYLE_PREFERENCES_MAX) {
        setError(`You can select up to ${STYLE_PREFERENCES_MAX} ${STYLE_PREFERENCE_LABELS[field]}.`);
        return prev;
      }

      const newArray = isRemoving
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleBooleanToggle = (field) => {
    setStyleData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleAddGenre = () => {
    if (genreInput.trim() && !(styleData.genres || []).includes(genreInput.trim())) {
      setStyleData(prev => ({
        ...prev,
        genres: [...(prev.genres || []), genreInput.trim()]
      }));
      setGenreInput('');
    }
  };

  const handleRemoveGenre = (genre) => {
    setStyleData(prev => ({
      ...prev,
      genres: (prev.genres || []).filter(g => g !== genre)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const weddingTypeCount = (styleData.weddingType || []).length;
    const vibeTagsCount = (styleData.vibeTags || []).length;

    if (weddingTypeCount < STYLE_PREFERENCES_MIN) {
      setError(`Please select at least ${STYLE_PREFERENCES_MIN} wedding type before continuing.`);
      return;
    }
    if (vibeTagsCount < STYLE_PREFERENCES_MIN) {
      setError(`Please select at least ${STYLE_PREFERENCES_MIN} style vibe tag before continuing.`);
      return;
    }
    if (weddingTypeCount > STYLE_PREFERENCES_MAX) {
      setError(`Please select no more than ${STYLE_PREFERENCES_MAX} wedding types.`);
      return;
    }
    if (vibeTagsCount > STYLE_PREFERENCES_MAX) {
      setError(`Please select no more than ${STYLE_PREFERENCES_MAX} style vibe tags.`);
      return;
    }

    if (!businessId) {
      setError('Business ID is required. Please complete previous steps first.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...styleData,
        formality: (styleData.formality && String(styleData.formality).trim()) ? styleData.formality : null
      };
      await api.put(`/api/supplier/business/${businessId}/style`, payload);
      
      onComplete({
        ...formData,
        styleData
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update style';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryFields = () => {
    const options = getOptionsForCategory();
    const performanceTypes = Array.isArray(styleData.performanceType)
      ? styleData.performanceType
      : (styleData.performanceType ? [styleData.performanceType] : []);

    if (!options || Object.keys(options).length === 0) {
      return (
        <Alert severity="info">
          No specific style fields for this category. You can skip this step.
        </Alert>
      );
    }

    return (
      <Grid container spacing={2}>
        {/* Venue Type */}
        {options.venueType && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Venue Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.venueType.map((type) => (
                <Chip
                  key={type}
                  label={type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                  onClick={() => handleArrayToggle('venueType', type)}
                  color={(styleData.venueType || []).includes(type) ? 'primary' : 'default'}
                  variant={(styleData.venueType || []).includes(type) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Style Specialization */}
        {options.styleSpecialization && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Style Specialization
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.styleSpecialization.map((style) => (
                <Chip
                  key={style}
                  label={style.charAt(0).toUpperCase() + style.slice(1).replace('_', ' ')}
                  onClick={() => handleArrayToggle('styleSpecialization', style)}
                  color={(styleData.styleSpecialization || []).includes(style) ? 'primary' : 'default'}
                  variant={(styleData.styleSpecialization || []).includes(style) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Capacity Specialization */}
        {options.capacitySpecialization && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Capacity Specialization
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.capacitySpecialization.map((size) => (
                <Chip
                  key={size}
                  label={size}
                  onClick={() => handleArrayToggle('capacitySpecialization', size)}
                  color={(styleData.capacitySpecialization || []).includes(size) ? 'primary' : 'default'}
                  variant={(styleData.capacitySpecialization || []).includes(size) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Style (for photographers/videographers) */}
        {options.style && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Photography/Videography Style
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.style.map((s) => (
                <Chip
                  key={s}
                  label={s.charAt(0).toUpperCase() + s.slice(1)}
                  onClick={() => handleArrayToggle('style', s)}
                  color={(styleData.style || []).includes(s) ? 'primary' : 'default'}
                  variant={(styleData.style || []).includes(s) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Features (checkboxes) */}
        {options.features && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Features
            </Typography>
            <FormGroup>
              {options.features.includes('secondShooterAvailable') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={styleData.secondShooterAvailable || false}
                      onChange={() => handleBooleanToggle('secondShooterAvailable')}
                    />
                  }
                  label="Second Shooter Available"
                />
              )}
              {options.features.includes('droneAvailable') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={styleData.droneAvailable || false}
                      onChange={() => handleBooleanToggle('droneAvailable')}
                    />
                  }
                  label="Drone Available"
                />
              )}
              {options.features.includes('onSiteService') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={styleData.onSiteService || false}
                      onChange={() => handleBooleanToggle('onSiteService')}
                    />
                  }
                  label="On-Site Service Available"
                />
              )}
            </FormGroup>
          </Grid>
        )}

        {/* Travel Radius */}
        {options.travelRadius && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Travel Radius (miles)"
              type="number"
              value={styleData.travelRadius || ''}
              onChange={(e) => setStyleData(prev => ({
                ...prev,
                travelRadius: parseFloat(e.target.value) || 0
              }))}
              variant="outlined"
              inputProps={{ min: 0 }}
            />
          </Grid>
        )}

        {/* Services */}
        {options.services && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Services Offered
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.services.map((service) => (
                <Chip
                  key={service}
                  label={service.charAt(0).toUpperCase() + service.slice(1).replace(/_/g, ' ')}
                  onClick={() => handleArrayToggle('services', service)}
                  color={(styleData.services || []).includes(service) ? 'primary' : 'default'}
                  variant={(styleData.services || []).includes(service) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Performance Type */}
        {options.performanceType && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Performance Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.performanceType.map((type) => (
                <Chip
                  key={type}
                  label={type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                  onClick={() => handleArrayToggle('performanceType', type)}
                  color={performanceTypes.includes(type) ? 'primary' : 'default'}
                  variant={performanceTypes.includes(type) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Music Genres (only for band, dj, music_live_entertainment) */}
        {['band', 'dj', 'music_live_entertainment'].includes(category) && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Music Genres
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add genre (e.g., Jazz, Rock, Pop)"
                value={genreInput}
                onChange={(e) => setGenreInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGenre();
                  }
                }}
              />
              <Button variant="outlined" onClick={handleAddGenre} disabled={!genreInput.trim()}>
                Add
              </Button>
            </Box>
            {(styleData.genres || []).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {styleData.genres.map((genre) => (
                  <Chip
                    key={genre}
                    label={genre}
                    onDelete={() => handleRemoveGenre(genre)}
                    color="primary"
                  />
                ))}
              </Box>
            )}
          </Grid>
        )}

        {/* Service Style (for caterers) */}
        {options.serviceStyle && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Service Style
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.serviceStyle.map((style) => (
                <Chip
                  key={style}
                  label={style.charAt(0).toUpperCase() + style.slice(1).replace(/_/g, ' ')}
                  onClick={() => handleArrayToggle('serviceStyle', style)}
                  color={(styleData.serviceStyle || []).includes(style) ? 'primary' : 'default'}
                  variant={(styleData.serviceStyle || []).includes(style) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Dietary Options */}
        {options.dietaryOptions && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Dietary Options
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.dietaryOptions.map((option) => (
                <Chip
                  key={option}
                  label={option.charAt(0).toUpperCase() + option.slice(1).replace(/_/g, ' ')}
                  onClick={() => handleArrayToggle('dietaryOptions', option)}
                  color={(styleData.dietaryOptions || []).includes(option) ? 'primary' : 'default'}
                  variant={(styleData.dietaryOptions || []).includes(option) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}

        {/* Specialization (for planners) */}
        {options.specialization && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
              Specialization
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.specialization.map((spec) => (
                <Chip
                  key={spec}
                  label={spec.charAt(0).toUpperCase() + spec.slice(1).replace(/_/g, ' ')}
                  onClick={() => handleArrayToggle('specialization', spec)}
                  color={(styleData.specialization || []).includes(spec) ? 'primary' : 'default'}
                  variant={(styleData.specialization || []).includes(spec) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        What's your wedding vibe?
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        We'll use this to match you with couples who love your style.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Overall Wedding Style Preferences */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, ...styles.text.primary }}>
            Overall Wedding Style Preferences
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, ...styles.text.secondary }}>
            These preferences help us match you with couples who share your style vision.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, ...styles.text.secondary }}>
            Select between 1 and 5 wedding types, and 1 and 5 style vibe tags. We recommend 3 to 5 in each.
          </Typography>

          <Grid container spacing={2}>
            {/* Wedding Types */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
                Wedding Types You Specialise In
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {supplierWeddingTypes.map((type) => (
                  <Chip
                    key={type}
                    label={formatOptionLabel(type)}
                    onClick={() => handleStylePreferenceToggle('weddingType', type)}
                    color={(styleData.weddingType || []).includes(type) ? 'primary' : 'default'}
                    variant={(styleData.weddingType || []).includes(type) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Grid>

            {/* Vibe Tags */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, ...styles.text.primary }}>
                Style Vibe Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {supplierVibeTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={formatOptionLabel(tag)}
                    onClick={() => handleStylePreferenceToggle('vibeTags', tag)}
                    color={(styleData.vibeTags || []).includes(tag) ? 'primary' : 'default'}
                    variant={(styleData.vibeTags || []).includes(tag) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Grid>

            {/* Formality */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Formality Level</InputLabel>
                <Select
                  value={styleData.formality || ''}
                  onChange={(e) => setStyleData({ ...styleData, formality: e.target.value })}
                  label="Formality Level"
                >
                  <MenuItem value="">Select...</MenuItem>
                  {supplierFormalityOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1).replace(/-/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Category-Specific Fields */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, ...styles.text.primary }}>
            Category-Specific Style
          </Typography>
          {renderCategoryFields()}
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{ mt: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Continue'}
        </Button>
      </form>
    </Box>
  );
}

export default Step5Style;

